import { useQuery, useQueryClient } from 'react-query';
import { useContext, useMemo } from 'react';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import * as api from '../../services/api';
import useOptimisticMutation from '../useOptimisticMutation';
import useToaster from '../useToaster';
import { LONG_POLLING_INTERVAL, REQUESTS_STATUS } from '../../constants';
import { CONFIRMABLE_LEAVE_ERRORS } from '../../constants/leave';
import { RequestTypes } from '../generic/useRequestsTypes';
import usePagination from '../usePagination';
import useAccount from './useAccount';
import useBranches from './useBranches';

export const REQUEST_JOB_TYPE_ID = {};

const useRequests = (params) => {
  const {
    enabled = true,
    pageSize = 50,
    search,
    requestsStatus = REQUESTS_STATUS.PENDING,
    selectedBranches,
    bookerId,
    // aliased: the bulk-status mutations below already bind `requestIds` to their own payloads
    requestIds: filterRequestIds
  } = params || {};
  const { t, enableContractFeature, enableContractLeaveManagementFeature } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const { branches } = useBranches();

  // requestIds belongs here (and not only in queryParams) so that adding or clearing the filter
  // sends the list back to page 1 - a 1-row filtered list has no page 3 to return to.
  const paginationParams = useMemo(
    () => ({
      defaultParams: { sortBy: 'id', sortDirection: 'DESC' },
      params: {
        accountId,
        pageSize,
        search,
        requestsStatus,
        bookerId,
        requestIds: filterRequestIds
      }
    }),
    [accountId, pageSize, requestsStatus, search, bookerId, filterRequestIds]
  );

  const { page, setPage, state, setState } = usePagination(paginationParams);

  const queryParams = useMemo(
    () => ({
      page,
      pageSize: state.pageSize,
      status: state.requestsStatus,
      accountId: state.accountId,
      searchTerm: state.search,
      bookerId: state.bookerId || undefined,
      requestIds: state.requestIds?.length ? state.requestIds : undefined,
      orderBy: [{ col: state.sortBy, dir: state.sortDirection }],
      branchIds: selectedBranches || branches.map(({ id }) => id)
    }),
    [
      branches,
      page,
      selectedBranches,
      state.accountId,
      state.pageSize,
      state.requestsStatus,
      state.search,
      state.bookerId,
      state.requestIds,
      state.sortBy,
      state.sortDirection
    ]
  );

  const storeKey = ['requests', JSON.stringify(queryParams)];
  const { isLoading, data, refetch } = useQuery(storeKey, () => api.fetchPaginatedRequests(queryParams), {
    refetchInterval: LONG_POLLING_INTERVAL,
    enabled
  });

  const { addSuccess, addResponseError } = useToaster();
  const queryClient = useQueryClient();

  const { mutateAsync: addRequest } = useOptimisticMutation(
    storeKey,
    ({ typeId, employeeId, branchId, jobTypeId, startTime, endTime }) =>
      api.addRequest({ accountId, typeId, branchId, employeeId, jobTypeId, startTime, endTime }),

    () => {},
    {
      refetchOnSuccess: false
    }
  );

  const { mutateAsync: deleteRequest } = useOptimisticMutation(
    storeKey,
    (requestId) => api.deleteRequest({ accountId, requestId }),
    ({ previousData, requestId }) => ({
      ...previousData,
      data: previousData.data.filter((request) => request.id !== requestId)
    }),
    {
      refetchOnSuccess: false,
      onSuccess: () => {
        queryClient.invalidateQueries(storeKey);
      }
    }
  );

  const { mutateAsync: updateRequestStatus } = useOptimisticMutation(
    storeKey,
    api.updateRequest,
    ({ previousData, requestId, status, managerReason }) => ({
      ...previousData,
      data:
        previousData &&
        previousData.data?.map((request) =>
          request.id === requestId ? { ...request, status, managerReason } : request
        )
    }),
    {
      refetchOnSuccess: false,
      showErrorToaster: false,
      onError: (error) => {
        const errorData = error?.response?.data;
        const code = errorData?.errorCode;
        if (!CONFIRMABLE_LEAVE_ERRORS.includes(code)) {
          addResponseError(error);
        }
      },
      onSuccess: ({ status, requestId }) => {
        addSuccess(t(status === 0 ? 'requestRestored' : status === 1 ? 'requestApproved' : 'requestRejected'));
        queryClient.invalidateQueries(['projects', accountId]);
        queryClient.invalidateQueries(['requests', accountId, 'stats']);
        // the scheduling board's leave/absence chips come from here - without this the chip the user
        // clicked to open this modal keeps saying "Pending" after they approve it
        queryClient.invalidateQueries(['timeAvailabilities']);

        if (status === 1) {
          const request = data?.data?.find((req) => req.id === requestId);

          if (enableContractFeature && request && request.typeId === RequestTypes.ABSENCE) {
            const { employeeId } = request;
            queryClient.invalidateQueries(['employeePto', accountId, employeeId], { exact: false });
          }

          // LEAVE_REQUEST approval lands both records + ledger events — refresh both trees.
          if (enableContractLeaveManagementFeature && request && request.typeId === RequestTypes.LEAVE_REQUEST) {
            const { employeeId } = request;
            queryClient.invalidateQueries(['employeeLeaveManagement', accountId, employeeId], { exact: false });
            queryClient.invalidateQueries(['employeeLeaveHistory', accountId, employeeId], { exact: false });
          }
        }
      }
    }
  );

  const { mutateAsync: updateBulkRequestStatuses } = useOptimisticMutation(
    storeKey,
    api.updateBulkRequests,
    ({ previousData, requestIds, status, managerReason }) => {
      const requestIdsSet = new Set(requestIds);
      return {
        ...previousData,
        data:
          previousData &&
          previousData.data?.map((request) =>
            requestIdsSet.has(request.id) ? { ...request, status, managerReason } : request
          )
      };
    },
    {
      refetchOnSuccess: false,
      onSuccess: ({ status, requestIds }) => {
        addSuccess(t(status === 0 ? 'requestsRestored' : status === 1 ? 'requestsApproved' : 'requestsRejected'));
        queryClient.invalidateQueries(['projects', accountId]);
        queryClient.invalidateQueries(['requests', accountId, 'stats']);
        // see the single-request mutation above - the board's chips are served from this tree
        queryClient.invalidateQueries(['timeAvailabilities']);

        if (status === 1) {
          const requestsById = new Map(data?.data?.map((req) => [req.id, req]));
          const ptoEmployeeIds = new Set();
          const leaveManagementEmployeeIds = new Set();

          // Check for relevant employees
          for (const requestId of requestIds || []) {
            const request = requestsById.get(requestId);
            if (enableContractFeature && request?.typeId === RequestTypes.ABSENCE && request?.employeeId) {
              ptoEmployeeIds.add(request.employeeId);
            }
            if (
              enableContractLeaveManagementFeature &&
              request?.typeId === RequestTypes.LEAVE_REQUEST &&
              request?.employeeId
            ) {
              leaveManagementEmployeeIds.add(request.employeeId);
            }
          }
          for (const employeeId of ptoEmployeeIds) {
            queryClient.invalidateQueries(['employeePto', accountId, employeeId], { exact: false });
          }
          for (const employeeId of leaveManagementEmployeeIds) {
            queryClient.invalidateQueries(['employeeLeaveManagement', accountId, employeeId], { exact: false });
            queryClient.invalidateQueries(['employeeLeaveHistory', accountId, employeeId], { exact: false });
          }
        }
      }
    }
  );

  return {
    page,
    setPage,
    pageSize,
    setSort: setState,
    isLoading,
    refetch,
    deleteRequest,
    addRequest,
    requests: data?.data || [],
    total: data?.total || 0,
    updateRequestStatus,
    updateBulkRequestStatuses
  };
};

export default useRequests;
